import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted } from 'vue';

import ProviderPromotionOffer from '@/components/ProviderPromotionOffer.vue';
import TitleDetailInfoRow from '@/pages/titleDetailV1/components/titleDetailInfoRow.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import TrailerModal from '@/components/TrailerModal.vue';
// JW_APP_DOWNLOAD_EXP_1
import { AppDownloadModal } from '@/lazy-load-components';
import { SnowplowClickoutContextGraphql } from '@/helpers/tracking/providers/snowplow-contexts';
// JW_APP_DOWNLOAD_EXP_1

import { TitleDetail } from '@/interfaces/title-details-graphql';
import { getBackdropClips } from '@/helpers/title-helper';
import { ModalHelper } from '@/helpers/modal-helper';
import { isMobile } from '@/helpers/ui-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ClipProvider, SraPlacement } from '@/@types/graphql-types';
import { SnowplowTitleContextGraphql, type SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { useAppStore, useLanguageStore, useRoutingStore, useUserStore } from '@/helpers/composables/useStores';
import { OfferLocation } from '@/helpers/offer-helper';
import { isJustWatchTVPlayBtnLanguageSupported } from '@/helpers/justwatchTv-helper';
import { TrailerClip } from '@/interfaces/trailerClip';
import { getTrailerComponent } from '@/helpers/trailer-helper';
import { observableOptions } from '@/helpers/composables/useOfferImpressionTracking';
import { useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { faPlay } from '@fortawesome/free-solid-svg-icons';

// SEASON_PAGE_BUYBOX_EXP
import { useProvideClickoutPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { ClickoutUrl, trackClickout } from '@/helpers/clickout-helper';
import { useProviderPromotion } from '@/helpers/provider-promotion';
import { useSendBidId } from '@/components/sponsored-recommendations/useSendBidId';
// SEASON_PAGE_BUYBOX_EXP

import { useActivateExperiment } from '@/libraries/experiment';
import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleDetailHeroV1',
  props: {
    title: null,
    originalTitle: null,
    seasonName: { default: null },
    AVODOffer: null,
    activeClipId: { default: null },
    isModal: { type: Boolean },
    additionalContexts: { default: () => [] }
  },
  emits: ['setActiveClipId', 'closeTitleDetailModal'],
  setup(__props: any, { emit: emits }) {

const props = __props as { title: TitleDetail, originalTitle?: string, seasonName: string | null, AVODOffer?: TitleOfferFragment, activeClipId: string | null, isModal?: boolean, additionalContexts: SnowplowContext[] };

const JustWatchTVPlayBtn = () => import('./JustwatchTVPlayBtn.vue');





const { language, country } = useLanguageStore();
const { activeRoute } = useRoutingStore();

const { xServeTrack } = useAppStore();
const { isLoggedIn } = useUserStore();

useProvideClickoutPlacement('backdrop');

// JW_APP_DOWNLOAD_EXP_1
const shouldActivateJwAppDlExperiment = computed(() => {
	return isMobile() && isJustWatchTV.value;
});

const { activeVariantWithControlGroup, trackEvent } = useActivateExperiment(
	'JW_APP_DOWNLOAD_EXP_1',
	shouldActivateJwAppDlExperiment
);
// JW_APP_DOWNLOAD_EXP_1

// Sponsored Recommendation

const { sponsoredAd } = useSendBidId({
	pageType: 'VIEW_TITLE_DETAIL',
	placement: SraPlacement.BackdropVideo,
});

// **** SRs Backdrop Experiment ****
const hasUserClickedOnTrailerBackdrop = ref(false);

const shouldActivateSrBackdropExperiment = computed(() => {
	return (
		sponsoredAd?.value?.campaign?.name === 'F1_BACKDROP_TEST_BURST1_IT_SRA' &&
		country.value === 'IT' &&
		hasUserClickedOnTrailerBackdrop.value
	);
});

const { activeVariantWithControlGroup: activeBackdropVariantWithControlGroup } = useActivateExperiment(
	'SR_BACKDROP_BB_IMPACT',
	shouldActivateSrBackdropExperiment
);
// **** SRs Backdrop Experiment ****

const trackingProviders: Record<TrailerClip['provider'], number> = {
	[ClipProvider.Youtube]: 1,
	[ClipProvider.Videobuster]: 2,
	[ClipProvider.Dailymotion]: 3,
	PLEX: 4,
	[ClipProvider.Justwatchtv]: 5,
	JUSTWATCH_TV: 5,
};

const isJustWatchTV = computed(
	() =>
		props.title.justwatchTVOffers.length > 0 &&
		props.title.justwatchTVOffers[0].package.technicalName === 'justwatchtv'
);

const titleClips = computed(() => {
	const PlexClip = props.AVODOffer
		? [
				{
					provider: 'PLEX',
					externalId: props.AVODOffer.id,
					sourceUrl: props.AVODOffer.standardWebURL,
				},
		  ]
		: null;

	const JustWatchTVClip = isJustWatchTV.value
		? [
				{
					provider: 'JUSTWATCH_TV',
					externalId: props.title.justwatchTVOffers[0].id,
					sourceUrl: props.title.justwatchTVOffers[0].streamUrl,
				},
		  ]
		: null;

	return JustWatchTVClip || PlexClip || getBackdropClips(props.title);
});

const { titleHasProviderPromotion } = useProviderPromotion(() => props.title);

const titleName = computed(() => {
	return props.title.__typename === 'Season' ? props.title.show.content.title : props.title.content.title;
});

const titleFullPath = computed(() => {
	return props.title.__typename === 'Season' ? props.title.show.content.fullPath : props.title.content.fullPath;
});

const releaseYear = computed(() => {
	return props.title.content.originalReleaseYear;
});

const showTitleId = computed(() =>
	props.title.__typename === 'Season' && !!props.title.show ? props.title.show.id : props.title.id
);

const showOriginalTitle = computed(() => !!props.originalTitle && props.originalTitle !== props.title.content.title);

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

// remove the empty backdrop space, if in the backdrop there is
// - no backdrop
// - no play trailer button
// - no play JWTV button
// - no provider promotion button
const isBackdropEmpty = computed(
	() =>
		props.title.content.backdrops.length === 0 && // no backdrop images
		!titleHasProviderPromotion.value && // no provider "play button in backdrop" promotion
		!(isJustWatchTV.value && isJustWatchTVPlayBtnLanguageSupported(language.value)) && // no JWTV
		titleClips.value.length === 0 // no trailers
);

const isMounted = ref(false);
const hasModalOpened = ref(false);
// we need an additional state that isn't tied to re-renderings, but prevents users from opening multiple modal windows.
const isModalOpening = ref(false);

const imageUrls = computed(() => props.title.content.backdrops.map(backdrop => backdrop.backdropUrl));

const observeVisibilityOptions = computed(() => {
	return !!offerForImpression.value
		? {
				callback: onVisibilityChanged,
				...observableOptions,
		  }
		: null;
});

const offerForImpression = computed(() => {
	if (isJustWatchTV.value) return props.title.justwatchTVOffers[0];
	if (props.AVODOffer) return props.AVODOffer;

	return null;
});

// create clickoutUrl for offerImpression tracking. Can pass the created contexts to the track event.
const clickoutUrl = computed(() => {
	if (!offerForImpression.value || titleHasProviderPromotion.value) return null;

	const objectType = props.title.__typename === 'Season' ? props.title.show.objectType : props.title.objectType;
	const objectId = props.title.__typename === 'Season' ? props.title.show.objectId : props.title.objectId;

	return new ClickoutUrl(offerForImpression.value)
		.setTitleSourceId(props.title?.id ?? null)
		.addContexts(new SnowplowTitleContextGraphql(objectId, objectType))
		.addContexts(props.additionalContexts)
		.setForTracking('offerLocation', OfferLocation.BACKDROP)
		.set('uct_country', country.value)
		.updateContext('ClickoutContextGraphql', 'clickoutType', 'video_player')
		.updateContext('ClickoutContextGraphql', 'placement', 'backdrop')
		.updateContext('TitleContextGraphql', 'offerEntityId', props.title?.id ?? null)
		.setCanary(xServeTrack.value);
});

function onVisibilityChanged(isVisible: boolean) {
	if (!!clickoutUrl.value) onIntersection(clickoutUrl.value, isVisible);

	// JW_APP_DOWNLOAD_EXP_1
	if (isVisible && activeVariantWithControlGroup.value) {
		trackEvent({
			action: 'impression',
			label: 'jwtv_offer',
			property: 'jwtv_offer',
			contexts: [SnowplowClickoutContextGraphql.fromProviderOffer(props.title.justwatchTVOffers[0])],
		});
	}
	// JW_APP_DOWNLOAD_EXP_1
}

function handleBackdropClick() {
	if (isLoggedIn.value && !isJustWatchTV.value) {
		return;
	}
	openTrailerModal(false);
	// for SR Backdrop Experiment
	hasUserClickedOnTrailerBackdrop.value = true;

	if (clickoutUrl.value) {
		// TODO: This is not a "clickout" it should be replace with a more relevant event
		trackClickout(clickoutUrl.value);
	}
}

async function openTrailerModal(nonInteraction: boolean) {
	if (titleClips.value.length === 0 || titleHasProviderPromotion.value) return;
	if (hasModalOpened.value || isModalOpening.value) return;
	isModalOpening.value = true;

	try {
		// INP case: artificially give the JS thread time to breathe by adding a re-render.
		hasModalOpened.value = true; // important to have this after the setTimeout, so the re-rendering is happening afterwards
		await new Promise(resolve => setTimeout(resolve, 0));

		TrackingHelper.trackEvent('youtube_started', {
			action: props.title.objectType.toLowerCase(),
			label: 'title-detail',
			value: trackingProviders[titleClips.value[0].provider as ClipProvider],
			nonInteraction,
		});

		emits('setActiveClipId', titleClips.value[0].externalId);
		// wait for modal to be closed again

		// JW_APP_DOWNLOAD_EXP_1
		if (['variant_1', 'variant_2'].includes(activeVariantWithControlGroup.value ?? '')) {
			trackEvent({
				action: 'click',
				label: 'jwtv_offer',
				property: 'jwtv_offer',
				contexts: [SnowplowClickoutContextGraphql.fromProviderOffer(props.title.justwatchTVOffers[0])],
			});

			ModalHelper.openModal(
				AppDownloadModal,
				{
					clips: titleClips,
					title: props.title,
					source: 'title-detail',
					additionalContexts: [...props.additionalContexts],
					variant: activeVariantWithControlGroup.value,
				},
				{ cssClass: 'trailer-modal' },
				'',
				true,
				[],
				() => (hasModalOpened.value = false)
			);

			trackEvent({
				action: 'impression',
				label: 'watch_now',
				property: 'watch_now',
				contexts: [SnowplowClickoutContextGraphql.fromProviderOffer(props.title.justwatchTVOffers[0])],
			});
		} else {
			if (activeVariantWithControlGroup.value === 'control') {
				trackEvent({
					action: 'click',
					label: 'jwtv_offer',
					property: 'jwtv_offer',
					contexts: [SnowplowClickoutContextGraphql.fromProviderOffer(props.title.justwatchTVOffers[0])],
				});
			}
			// JW_APP_DOWNLOAD_EXP_1

			await ModalHelper.openModal(
				TrailerModal,
				{
					clips: titleClips,
					title: props.title,
					source: 'title-detail',
					additionalContexts: props.additionalContexts,
					sponsoredAd: sponsoredAd.value,
					sraPlacement: SraPlacement.BackdropVideo,
					activeVariantWithControlGroup: activeBackdropVariantWithControlGroup.value,
				},
				{ cssClass: 'trailer-modal' },
				'',
				true,
				[],
				() => (hasModalOpened.value = false)
			);
		}
	} catch (e) {
		console.error(e);
	} finally {
		isModalOpening.value = false;
		hasModalOpened.value = false;
	}
}

function handleClickTitleDetailLink() {
	// navigation is to same url, so the modal is not destroyed
	if (props.isModal && titleFullPath.value) {
		emits('closeTitleDetailModal');
	}

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'title_clicked',
			label: showTitleId.value,
			property: 'title-header',
		},
		props.additionalContexts
	);
}

onMounted(() => {
	if (props.activeClipId) {
		emits('setActiveClipId', null);
	}
	isMounted.value = true;
	TrackingHelper.trackEvent('clip_spinner', {
		action: 'finished',
		value: 1,
		property: titleClips.value.length > 0 ? titleClips.value[0].provider : 'none',
		nonInteraction: true,
	});

	// INP: preload the trailer component's JS already for slow devices
	getTrailerComponent(titleClips.value[0]?.provider as ClipProvider);

	setTimeout(() => {
		const query = activeRoute.value.query;
		if (query['trailer'] === 'play') {
			openTrailerModal(true);
		}
	});
});

watch(activeRoute, () => {
	emits('setActiveClipId', null);
});

return { __sfc: true,JustWatchTVPlayBtn, props, emits, language, country, activeRoute, xServeTrack, isLoggedIn, shouldActivateJwAppDlExperiment, activeVariantWithControlGroup, trackEvent, sponsoredAd, hasUserClickedOnTrailerBackdrop, shouldActivateSrBackdropExperiment, activeBackdropVariantWithControlGroup, trackingProviders, isJustWatchTV, titleClips, titleHasProviderPromotion, titleName, titleFullPath, releaseYear, showTitleId, showOriginalTitle, onIntersection, isBackdropEmpty, isMounted, hasModalOpened, isModalOpening, imageUrls, observeVisibilityOptions, offerForImpression, clickoutUrl, onVisibilityChanged, handleBackdropClick, openTrailerModal, handleClickTitleDetailLink, ProviderPromotionOffer, TitleDetailInfoRow, TitleDetailLink, PictureComp, isJustWatchTVPlayBtnLanguageSupported, faPlay }
}

})